import { useState, useEffect, useRef } from 'react';
import api from '../services/api';

/**
 * Hook para lazy loading de conteúdo completo do artigo
 * Segue Atomic Design - lógica extraída do ArticleModal
 */
const useLazyLoadContent = (article, isOpen) => {
  const [localArticle, setLocalArticle] = useState(article);
  const [loading, setLoading] = useState(false);

  // Ref para rastrear o último artigo carregado e evitar carregamentos duplos
  const lastLoadedIdRef = useRef(null);
  const isLoadingRef = useRef(false);

  // Sincronizar localArticle imediatamente quando article muda (importante para navegação entre artigos)
  // Usa apenas article._id como dependência para evitar loops
  useEffect(() => {
    if (article && article._id !== localArticle?._id) {
      setLocalArticle(article);
      // Reset do lastLoadedId quando muda de artigo para permitir novo carregamento
      lastLoadedIdRef.current = null;
    }
  }, [article?._id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const loadFullContent = async () => {
      // Reset quando modal fecha - garante que ao reabrir sempre busca conteúdo atualizado
      if (!isOpen) {
        lastLoadedIdRef.current = null;
        return;
      }

      // Se não há artigo, não fazer nada
      if (!article) {
        setLocalArticle(null);
        return;
      }

      // Evitar carregamento duplo do mesmo artigo
      if (lastLoadedIdRef.current === article._id || isLoadingRef.current) {
        return;
      }

      // Se o artigo já tem conteúdo E tem embedCode (quando necessário), usar diretamente
      const needsEmbedCode = ['TikTok', 'Facebook', 'Instagram', 'Threads'].includes(article.mediaType);
      const hasEmbedCode = article.embedCode || article.embedHtml;
      const looksLikeVideoArticle =
        article.contentType === 'video' ||
        article.contentType === 'reel' ||
        article.contentType === 'short' ||
        article.contentType === 'story' ||
        article.mediaType === 'Online' ||
        article.videoStorageMethod === 'cloudinary';
      const hasVideoSource = Boolean(article.videoUrl || article.videoId || article.youtubeId);
      const hasEditorialOccupancyDetails = Array.isArray(article.editorialOccupancy?.entityBreakdown) &&
        article.editorialOccupancy.entityBreakdown.some(entity =>
          entity?.name && ((entity.participationPercent || 0) > 0 || (entity.areaCm2 || 0) > 0)
        );
      const shouldRefreshForMetrics = !article.editorialOccupancy?.calculatedAt || !hasEditorialOccupancyDetails;
      const isPrintMedia = ['Jornal', 'Revista'].includes(article.mediaType);
      const shouldRefreshForPrintPages = isPrintMedia && (!Array.isArray(article.pages) || (article.pages.length <= 1 && !Array.isArray(article.allImages)));

      if (article.content && (!needsEmbedCode || hasEmbedCode) && (!looksLikeVideoArticle || hasVideoSource) && !shouldRefreshForMetrics && !shouldRefreshForPrintPages) {
        setLocalArticle(article);
        lastLoadedIdRef.current = article._id;
        return;
      }

      // Artigo não tem conteúdo - fazer lazy load
      setLoading(true);
      isLoadingRef.current = true;

      try {
        const response = await api.get(`/articles/${article._id}`);
        const fullArticle = response.data.data;

        // Mesclar o artigo completo com o artigo recebido
        setLocalArticle({
          ...article,
          ...fullArticle,
          // Manter campos originais que podem ter sido atualizados
          isFavorite: article.isFavorite,
          isRead: article.isRead
        });
        lastLoadedIdRef.current = article._id;
      } catch (error) {
        console.error('Erro ao carregar conteúdo completo:', error);
        // Mesmo com erro, usar o artigo que temos
        setLocalArticle(article);
      } finally {
        setLoading(false);
        isLoadingRef.current = false;
      }
    };

    loadFullContent();
  }, [article?._id, isOpen]); // Usar article._id em vez de article para evitar re-execução por mudança de referência

  // Sincronizar campos específicos quando article muda
  useEffect(() => {
    if (localArticle && article) {
      const needsUpdate = (
        localArticle.isFavorite !== article.isFavorite ||
        localArticle.isRead !== article.isRead ||
        JSON.stringify(localArticle.tags) !== JSON.stringify(article.tags)
      );

      if (needsUpdate) {
        setLocalArticle(prev => ({
          ...prev,
          isFavorite: article.isFavorite,
          isRead: article.isRead,
          tags: article.tags
        }));
      }
    }
  }, [article?.isFavorite, article?.isRead, article?.tags]);

  return {
    localArticle,
    setLocalArticle,
    loading
  };
};

export default useLazyLoadContent;


